import { useTranslation } from 'react-i18next'
import { Checkbox } from '../../components/Checkbox'
import { useOnboardingStore } from '../../store/onboardingStore'
import { useTenant } from '../../context/TenantContext'
import type { ItalyConsents } from '../../types'

const DEFAULT_TERMS_URL = 'https://gam-medical.com/termini-e-condizioni/'
const DEFAULT_PRIVACY_URL = 'https://gam-medical.com/privacy-policy/'

/** Italy cannot confirm the summary without accepting terms + privacy. */
export function isItalyConsentsValid(consents: ItalyConsents): boolean {
  return !!consents.termsPrivacy
}

/**
 * Italy consents on the summary step (confirmed list, 2026-09-03):
 *   1. marketing opt-in (optional) → PVS consent_type `marketing`
 *   2. terms & conditions + privacy acknowledgement (MANDATORY) → stored on the
 *      submission only (no PVS consent type for it)
 *
 * The earlier provisional five (health data access, post-visit survey, unsigned
 * report e-mail, televisit recording) are no longer asked; their store/DB fields
 * remain and are simply sent as false. Self-contained; DE/ES never render this.
 */
export function ItalyConsentFields() {
  const { t } = useTranslation()
  const { tenant } = useTenant()
  const { italyConsents, setItalyConsents } = useOnboardingStore()
  const termsUrl = tenant?.termsUrl || DEFAULT_TERMS_URL
  const privacyUrl = tenant?.privacyUrl || DEFAULT_PRIVACY_URL

  const link = (href: string, text: string) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">
      {text}
    </a>
  )

  return (
    <div className="bg-white rounded-card p-6 mb-6">
      <h3 className="text-h3 text-foreground mb-4">{t('italyConsents.heading')}</h3>
      <div className="space-y-3">
        <Checkbox
          id="consent-marketing"
          checked={italyConsents.marketing}
          onChange={(e) => setItalyConsents({ marketing: e.target.checked })}
          label={t('italyConsents.marketingOptIn')}
        />
        <Checkbox
          id="consent-termsPrivacy"
          checked={italyConsents.termsPrivacy}
          onChange={(e) => setItalyConsents({ termsPrivacy: e.target.checked })}
          label={
            <span>
              {t('italyConsents.acceptPrefix')}
              {link(termsUrl, t('italyConsents.termsLink'))}
              {t('italyConsents.acceptMiddle')}
              {link(privacyUrl, t('italyConsents.privacyLink'))}
              {t('italyConsents.acceptSuffix')} *
            </span>
          }
        />
      </div>
    </div>
  )
}
